/**
 * /admin/qa-checklist — interactive view of /app/memory/SCHEDULED_TASKS.md.
 *
 * Renders the QA checklist as live tickboxes, persists tick state to
 * localStorage so progress survives page reloads. Reusable across the
 * Friday QA day AND the pre-deploy checklist (just point at a different
 * data source).
 *
 * Admin/staff/owner gated. Owners use this on QA day to work through the
 * copy review + admin functional pass without flipping between this app
 * and a markdown file in another tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ClipboardCheck, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { QA_CHECKLIST } from '../data/qa-checklist';
import PageTitle from '../components/PageTitle';

const STORAGE_KEY = 'forge.qa-checklist.v1';

const loadTicks = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
const saveTicks = (ticks) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ticks)); } catch { /* private mode no-op */ }
};

const AdminQAChecklist = () => {
  const { user } = useAuth();
  const [ticks, setTicks] = useState({});

  useEffect(() => { setTicks(loadTicks()); }, []);

  const allItems = useMemo(
    () => QA_CHECKLIST.flatMap((s) => s.groups.flatMap((g) => g.items)),
    [],
  );
  const completedCount = allItems.filter((i) => ticks[i.id]).length;
  const totalCount = allItems.length;
  const pct = totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);

  const toggle = (id) => {
    setTicks((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveTicks(next);
      return next;
    });
  };

  const resetAll = () => {
    if (!window.confirm('Clear every tick? This cannot be undone.')) return;
    setTicks({});
    saveTicks({});
  };

  if (!user) return <Navigate to="/login" replace />;
  if (!isStaffOrAbove(user)) return <Navigate to="/home" replace />;

  return (
    <div className="p-6 sm:p-8 max-w-4xl mx-auto" data-testid="qa-checklist-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <PageTitle
          title="QA Day checklist"
          subtitle="Source of truth: /app/memory/SCHEDULED_TASKS.md. Ticks save to your browser."
          className="mb-0"
        />
        <Button onClick={resetAll} className="bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]" data-testid="qa-checklist-reset">
          <RotateCcw className="w-4 h-4 mr-2" />Reset
        </Button>
      </div>

      {/* Progress bar */}
      <div className="mb-8" data-testid="qa-checklist-progress">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-xs uppercase tracking-wider font-bold text-[#64748B]">Progress</span>
          <span className="text-sm font-extrabold text-[#020617]">{completedCount} / {totalCount} · {pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#E2E8F0] overflow-hidden">
          <div
            className="h-full bg-[#0052FF] transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="space-y-10">
        {QA_CHECKLIST.map((section) => (
          <Section key={section.section} section={section} ticks={ticks} onToggle={toggle} />
        ))}
      </div>
    </div>
  );
};

const Section = ({ section, ticks, onToggle }) => {
  const sectionItems = section.groups.flatMap((g) => g.items);
  const sectionDone = sectionItems.filter((i) => ticks[i.id]).length;
  return (
    <section data-testid={`qa-section-${section.section.replace(/\W+/g, '-').toLowerCase()}`}>
      <h2 className="text-xl font-extrabold text-[#020617] tracking-tight mb-1">{section.section}</h2>
      <p className="text-sm text-[#64748B] mb-1">{section.blurb}</p>
      <p className="text-xs font-medium text-[#0052FF] mb-4">{sectionDone} / {sectionItems.length} done</p>
      <div className="space-y-5">
        {section.groups.map((g) => (
          <Group key={g.title} group={g} ticks={ticks} onToggle={onToggle} />
        ))}
      </div>
    </section>
  );
};

const Group = ({ group, ticks, onToggle }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
    <h3 className="text-sm font-bold text-[#020617] mb-3">{group.title}</h3>
    <ul className="space-y-2">
      {group.items.map((item) => {
        const checked = !!ticks[item.id];
        return (
          <li key={item.id} className="flex items-start gap-3" data-testid={`qa-item-${item.id}`}>
            <Checkbox
              checked={checked}
              onCheckedChange={() => onToggle(item.id)}
              className="mt-0.5"
              aria-label={item.label}
            />
            <span className={`text-sm ${checked ? 'text-[#94A3B8] line-through' : 'text-[#334155]'}`}>
              {item.label}
            </span>
          </li>
        );
      })}
    </ul>
  </div>
);

export default AdminQAChecklist;
